import React, { useState } from 'react';
import { InformationCircleIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import PropertyCalculator from './PropertyCalculator';
import RoundRobinCycleDiagram from './RoundRobinCycleDiagram';

const Tech: React.FC = () => {
  const [showMoreExamples, setShowMoreExamples] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="tech" className="bg-gray-900" style={{ paddingTop: '48px', paddingBottom: '24px', marginTop: '0px', scrollMarginTop: '120px', borderTop: '1px solid #60a5fa' }}>
      <div className="container mx-auto px-6">
        
        {/* Secure by design - top of Tech */}
        <div className="mb-16 px-4 sm:px-8 lg:px-16 py-8 rounded-lg border border-gray-700 bg-gray-800/50" style={{ lineHeight: '1.5' }}>
          <h2 className="text-3xl font-bold mb-6 text-gray-200" style={{ color: '#60a5fa' }}>Secure by design</h2>
          <div className="text-gray-400 space-y-4 leading-relaxed max-w-4xl">
            <p>
              BornFree is built on a privacy-first, zero-access encryption architecture. Member data and NFT assets are protected so that even platform operators cannot access private keys or decrypt sensitive information. We integrate <strong className="text-gray-300">VetKey</strong> and cutting-edge key management so that private keys remain secure at all times—in storage, in transit, and at signing.
            </p>
            <p>
              All NFT minting, signing, and key operations run inside this ultra-secure environment. Members keep full control of their identities and assets while the system enforces decentralization and compliance. This modern architecture is designed for the long term: secure, auditable, and ready for the future of Web3.
            </p>
          </div>
        </div>
        
        {/* RWA Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start mb-16">
          {/* Left Column - Technology */}
          <div className="space-y-6" style={{ paddingLeft: '80px', paddingRight: '32px', paddingTop: '32px', paddingBottom: '32px', lineHeight: '1.5' }}>
            <h2 className="text-4xl font-bold mb-8 text-gray-300">Technology</h2>
                        <div className="text-gray-400 space-y-4 leading-relaxed mb-8">
              <p>
                Built on cutting-edge blockchain technology, BornFree leverages secure, transparent infrastructure to ensure trust and immutability for all community interactions. Our battle-tested smart contracts enable automated, trustless execution of community agreements and Key functionality, while decentralized governance gives every member a voice in the ecosystem's future.
              </p>
            </div>
               
              
              {/* RWA Section */}
              <div className="mt-12">
                <h2 className="font-bold mb-6 text-gray-300" style={{ fontSize: '1.5rem' }}>RWA - Real World Assets</h2>
                
                <div className="text-gray-400 space-y-4 leading-relaxed">
                  <p>
                    BornFree is not just a digital experiment — it's rooted in real-world assets (RWAs), from community spaces and eco-villages to immersive retreats and media projects. By tokenizing access and utility through Keys, the DAO provides community input that helps bridge blockchain coordination with tangible places, products, and people. These assets are managed by the SPC with community advisory input, with credit-backed systems that enable transparency, fairness, and shared participation.
                  </p>
                  
                  <p>
                    Guiding this evolution is the BornFree Seed Team — a collective of visionaries, technologists, and industry professionals. With decades of combined experience in real estate, supply chain management, finance, IT security, community development, and global media, the team ensures that every on-chain action connects back to real impact. From the U.S. to South America to East Asia, our advisors bring the operational and legal insight needed to expand BornFree's reach into physical communities, projects, and sustainable ventures. Whether through decentralized yields or community-powered development, the team's mission is to build a system that works both on-chain and on the ground.
                  </p>
                  
                  <p>
                    Ready to help shape the future? Join our governance and DAO formation on Discord — your voice matters here.
                  </p>
                </div>
              </div>
              
              {/* The Numbers Section */}
              <div className="mt-12">
                <h2 className="text-4xl font-bold mb-8" style={{ color: '#60a5fa' }}>The Numbers</h2>
                
                <div className="text-gray-400 space-y-4 leading-relaxed">
                  <p>
                    Each property is funded by selling Key Credits that represent future community access. Members receive 200 days of reservation priority per property each year through a Round Robin system, giving them first access to the most desirable dates. Remaining 165 days are used by the DAO to generate revenue and fund operations—keeping member costs at zero after their initial Key purchase.
                  </p>
                  
                  <p>
                    DAO-listed stays also introduce non-members to the community, helping grow participation and demand. Being a member offers better value, earlier access, and direct governance power.
                  </p>
                  
                  <p>
                    It is important to note that no real estate title, interest or appreciation is expected or implied. Real Estate prices historically rise with inflation as an outside market condition not in the control of the DAO or community. Members must participate through making and merging with other members to keep the community moving.
                  </p>
                </div>
                
                {/* Why All the Making and Merging? Subsection */}
                <div className="mt-8">
                  <h3 className="font-bold mb-6 text-gray-300" style={{ fontSize: '1.5rem' }}>Making and Merging</h3>
                  
                  <p className="text-gray-400 leading-relaxed">
                    BornFree isn't a passive investment—it's a participatory community. Making, merging, and donating credits empowers members to actively shape the project: securing better access, supporting new locations, and strengthening the reserve. The DAO only handles basic coordination and platform upkeep—real growth depends on members taking action. When you merge for access, make a new key to support expansion, or donate to earn perks, you're helping the entire ecosystem thrive with fairness, transparency, and purpose.
                  </p>
                </div>
                
                {/* The Lineup Process Section */}
                <div className="mt-12">
                  <h2 className="text-4xl font-bold mb-8" style={{ color: '#60a5fa' }}>The Lineup Process</h2>
                  
                  <div className="text-gray-400 space-y-4 leading-relaxed">
                    <p>
                      Our system isn't built around any one property or date — it's built around <strong style={{ color: '#60a5fa' }}>credits</strong> that hold universal value across all experiences, locations, and services. This gives our members full flexibility, no matter where they end up going.
                    </p>
                    
                    <p>
                      When we launch a new Experience Key collection, it's tied to a <strong style={{ color: '#60a5fa' }}>primary target property</strong> — but to ensure success even in uncertain real estate conditions, we always prepare a <strong style={{ color: '#60a5fa' }}>Lineup</strong> of three properties:
                    </p>
                    
                    <ul className="list-disc ml-6 space-y-2">
                      <li><strong style={{ color: '#60a5fa' }}>1 Target Property:</strong> The primary location we aim to secure for the Key drop</li>
                      <li><strong style={{ color: '#60a5fa' }}>2 Backup Properties:</strong> Alternates in case the target property cannot be acquired due to funding shortfalls or failed negotiations</li>
                    </ul>
                    
                    <p className="pt-4">
                      If for any reason the target property is not secured:
                    </p>
                    
                    <ul className="list-disc ml-6 space-y-2">
                      <li>Your <strong style={{ color: '#60a5fa' }}>credits remain unchanged</strong></li>
                      <li>Your <strong style={{ color: '#60a5fa' }}>Round Robin position is preserved</strong></li>
                      <li>A <strong style={{ color: '#60a5fa' }}>new Experience Key is issued</strong> for the backup location</li>
                      <li>The original Key is <strong style={{ color: '#60a5fa' }}>automatically burned</strong></li>
                    </ul>
                    
                    <p className="pt-4">
                      This system ensures our community always moves forward, even when individual deals don't — and no member is left behind.
                    </p>
                  </div>
                </div>
                
                {/* Property Example Section */}
                <div className="mt-12">
                  <h3 className="font-bold mb-6" style={{ fontSize: '1.5rem', color: '#60a5fa' }}>Examples</h3>
                  
                  <div className="text-gray-400 space-y-4 leading-relaxed mb-8">
                    <p>
                      Property purchase proposals can be made by any member and then voted on by the community to provide advisory input to the SPC. Members can locate potential locations from websites like <a href="https://www.loopnet.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">loopnet.com</a> and <a href="https://www.bizbuysell.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">bizbuysell.com</a>, <a href="https://www.zillow.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">zillow.com</a>, <a href="https://www.crexi.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">crexi.com</a> and other nation specific listing sites like <a href="https://www.idealista.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">idealista.com</a>, and <a href="https://www.adondevivir.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">adondevivir.com</a>.
                    </p>
                  </div>
                  
                  <img 
                    src="/images/Peru-EcoVillage.jpg" 
                    alt="Amazon Eco Lodge Tarapoto Property" 
                    className="w-full rounded-lg mb-6"
                    style={{ maxHeight: '400px', objectFit: 'cover', marginTop: '4rem' }}
                  />
                  
                  <h4 className="text-2xl font-bold text-gray-300 mb-2">Tarapoto, Peru — 22-Acre Eco Lodge</h4>
                  <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                    Shimiyacu Amazon Lodge<br />
                    10 Units, $850,000<br />
                    <a href="https://www.adondevivir.com/propiedades/clasificado/veclhtin-hermoso-amazon-eco-lodge-en-venta-tarapoto-141897824.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a><br />
                    <a href="https://www.tripadvisor.com/Hotel_Review-g658384-d4493258-Reviews-Shimiyacu_Amazon_Lodge-Tarapoto_San_Martin_Region.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Reviews</a><br />
                    22 acres of tropical forest adjacent to the river with a profitable ecolodge: 7 minutes from downtown Tarapoto and 15 minutes from the airport; Event room, Dining room, Professional kitchen; 7x3 pool, 3 2x3 storage rooms, Laundry and covered clothesline; Spring, Water well, Water and sewage networks. Wireless internet, Solar systems, 300 meters of stone paths. Access bridge, Fruit and timber plantations, Fish ponds
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px',
                    position: 'relative'
                  }}>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                      aria-label="Show calculator key"
                    >
                      <InformationCircleIcon className="h-7 w-7" />
                    </button>
                    <div className="overflow-x-auto text-xs">
                      <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                      </div>
                      <div className="divide-y divide-gray-700 text-gray-300">
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Units:</span> <span className="font-semibold">10</span></div>
                          <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 10 = 1650</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 10 = 2000</span></div>
                          <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$75</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">1,000,000 (500/night)</span></div>
                          <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$123,750</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                          <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$49,500</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$1,000,000</span></div>
                          <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$6,500</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$850,000</span></div>
                          <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$56,000</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                          <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$36,500</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Reserve Fund (15.3%):</span> <span className="font-semibold">$130,000</span></div>
                          <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$19,500</span></div>
                        </div>
                      </div>
                    </div>
                    <details className="mt-4">
                      <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                        How the numbers are calculated
                      </summary>
                      <div style={{ 
                        backgroundColor: '#111827', 
                        padding: '16px', 
                        borderRadius: '6px', 
                        border: '1px solid #374151',
                        marginTop: '8px'
                      }}>
                        <p className="text-gray-400 text-sm mb-4">
                          The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                        </p>
                        <p className="text-gray-400 text-sm mb-4">
                          Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                        </p>
                  </div>
                    </details>
                </div>
                  
                  {/* Blackpool Example */}
                  <img 
                    src="/images/Properties/330-Promenade-Blackpool-Primary-Photo-1-LargeHighDefinition.jpg" 
                    alt="Rutland Hotel Property" 
                    className="w-full rounded-lg mb-6"
                    style={{ maxHeight: '400px', objectFit: 'cover', marginTop: '6rem' }}
                  />
                  
                  <h4 className="text-2xl font-bold text-gray-300 mb-2">Blackpool, UK — 19-Room Hotel</h4>
                  <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                    Rutland Hotel<br />
                    330 Promenade, Blackpool FY1 2JG<br />
                    19 Room Hotel, $640,239<br />
                    <a href="https://www.loopnet.com/Listing/330-Promenade-Blackpool/33350946/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px',
                    position: 'relative'
                  }}>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                      aria-label="Show calculator key"
                    >
                      <InformationCircleIcon className="h-7 w-7" />
                    </button>
                    <div className="overflow-x-auto text-xs">
                      <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                      </div>
                      <div className="divide-y divide-gray-700 text-gray-300">
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Units:</span> <span className="font-semibold">19</span></div>
                          <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 19 = 3135</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 19 = 3800</span></div>
                          <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$90</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">3,990,000 (1050/night)</span></div>
                          <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$282,150</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                          <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$112,860</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$3,990,000</span></div>
                          <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$166,500</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$640,000</span></div>
                          <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$279,360</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                          <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$277,400</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Reserve Fund (520.3%):</span> <span className="font-semibold">$3,330,000</span></div>
                          <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$1,960</span></div>
                        </div>
                      </div>
                    </div>
                    <details className="mt-4">
                      <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                        How the numbers are calculated
                      </summary>
                      <div style={{ 
                        backgroundColor: '#111827', 
                        padding: '16px', 
                        borderRadius: '6px', 
                        border: '1px solid #374151',
                        marginTop: '8px'
                      }}>
                        <p className="text-gray-400 text-sm mb-4">
                          The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                        </p>
                        <p className="text-gray-400 text-sm mb-4">
                          Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                        </p>
                      </div>
                    </details>
                  </div>
                  
                  {/* Daytona Beach Example */}
                  <img 
                    src="/images/1915-N-Atlantic-Ave-Daytona-Beach-FL-Building-Photo-4-LargeHighDefinition.jpg" 
                    alt="Daytona Beach FL Hotel Property" 
                    className="w-full rounded-lg mb-6"
                    style={{ maxHeight: '400px', objectFit: 'cover', marginTop: '6rem' }}
                  />
                  
                  <h4 className="text-2xl font-bold text-gray-300 mb-2">Daytona Beach, FL — 8 Room Hotel</h4>
                  <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                    1915 Beach Club<br />
                    1915 N Atlantic Ave, Daytona Beach, FL 32118<br />
                    $1,399,000<br />
                    <a href="https://www.loopnet.com/Listing/1915-N-Atlantic-Ave-Daytona-Beach-FL/35070400/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                  </div>

                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px',
                    position: 'relative'
                  }}>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                      aria-label="Show calculator key"
                    >
                      <InformationCircleIcon className="h-7 w-7" />
                    </button>
                    <div className="overflow-x-auto text-xs">
                      <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                      </div>
                      <div className="divide-y divide-gray-700 text-gray-300">
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Units:</span> <span className="font-semibold">8</span></div>
                          <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 8 = 1320</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 8 = 1600</span></div>
                          <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$100</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">2,720,000 (1700/night)</span></div>
                          <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$132,000</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                          <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$52,800</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$2,720,000</span></div>
                          <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$65,050</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$1,399,000</span></div>
                          <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$117,850</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                          <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$116,800</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Reserve Fund (93.0%):</span> <span className="font-semibold">$1,301,000</span></div>
                          <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$1,050</span></div>
                        </div>
                      </div>
                    </div>
                    <details className="mt-4">
                      <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                        How the numbers are calculated
                      </summary>
                      <div style={{ 
                        backgroundColor: '#111827', 
                        padding: '16px', 
                        borderRadius: '6px', 
                        border: '1px solid #374151',
                        marginTop: '8px'
                      }}>
                        <p className="text-gray-400 text-sm mb-4">
                          The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                        </p>
                        <p className="text-gray-400 text-sm mb-4">
                          Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                        </p>
                      </div>
                    </details>
                  </div>
                  

                  {/* Hilo Hawaii Example */}
                  <img 
                    src="/images/333-Iliahi-St-Hilo-HI-Primary-Photo-1-HighDefinition.jpg" 
                    alt="Hilo Hotel Property" 
                    className="w-full rounded-lg mb-6"
                    style={{ maxHeight: '400px', objectFit: 'cover', marginTop: '6rem' }}
                  />
                  
                  <h4 className="text-2xl font-bold text-gray-300 mb-2">Hilo, HI — 18-Room Boutique Hotel</h4>
                  <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                    Dolphin Bay Boutique Hotel<br />
                    333 Iliahi St, HI 96720<br />
                    18 Room Hotel-Hilo, $3,200,000<br />
                    <a href="https://www.loopnet.com/Listing/333-Iliahi-St-Hilo-HI/33975309/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                  </div>
                  
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px',
                    position: 'relative'
                  }}>
                    <button 
                      onClick={() => setIsModalOpen(true)}
                      className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                      aria-label="Show calculator key"
                    >
                      <InformationCircleIcon className="h-7 w-7" />
                    </button>
                    <div className="overflow-x-auto text-xs">
                      <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                        <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                      </div>
                      <div className="divide-y divide-gray-700 text-gray-300">
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Units:</span> <span className="font-semibold">18</span></div>
                          <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 18 = 2,970</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 18 = 3600</span></div>
                          <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$150</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">5,040,000 (1400/night)</span></div>
                          <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$445,500</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                          <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$178,200</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$5,040,000</span></div>
                          <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$91,000</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$3,200,000</span></div>
                          <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$269,200</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                          <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$262,800</span></div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                          <div><span className="text-gray-500">Reserve Fund (56.9%):</span> <span className="font-semibold">$1,820,000</span></div>
                          <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$6,400</span></div>
                        </div>
                      </div>
                    </div>
                    <details className="mt-4">
                      <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                        How the numbers are calculated
                      </summary>
                      <div style={{ 
                        backgroundColor: '#111827', 
                        padding: '16px', 
                        borderRadius: '6px', 
                        border: '1px solid #374151',
                        marginTop: '8px'
                      }}>
                        <p className="text-gray-400 text-sm mb-4">
                          The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                        </p>
                        <p className="text-gray-400 text-sm mb-4">
                          Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                        </p>
                      </div>
                    </details>
                  </div>
                  
                  {/* SEE MORE POSSIBILITIES Button */}
                  <div className="mt-12 text-center">
                    <button 
                      onClick={() => setShowMoreExamples(!showMoreExamples)}
                      className="inline-flex items-center text-blue-400 hover:text-blue-300 font-medium text-lg transition-colors duration-200"
                    >
                      SEE MORE POSSIBILITIES
                      <svg 
                        className={`ml-2 w-5 h-5 transform transition-transform duration-200 ${showMoreExamples ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Expanded Examples Section */}
                  {showMoreExamples && (
                    <div className="mt-8 space-y-12">
                      {/* Additional Example 1 - Ocean Backpackers Hostel */}
                      <div>
                        <img 
                          src="/images/Properties/29-St-James-Pl-Ilfracombe-Primary-Photo-1-LargeHighDefinition.jpg" 
                          alt="Ocean Backpackers Hostel Property" 
                          className="w-full rounded-lg mb-6"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                        />
                        
                        <h4 className="text-2xl font-bold text-gray-300 mb-2">Ilfracombe, UK — 13-Room Hostel</h4>
                        <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                          Ocean Backpackers Hostel<br />
                          29 St. James Pl, Ilfracombe EX34 9BJ<br />
                          13 Room Hostel, $739,982<br />
                          <a href="https://www.loopnet.com/Listing/29-St-James-Pl-Ilfracombe/33621142/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                        </div>
                        
                        <div style={{ 
                          backgroundColor: '#1f2937', 
                          padding: '16px', 
                          borderRadius: '8px', 
                          border: '1px solid #4b5563',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          marginBottom: '20px',
                          position: 'relative'
                        }}>
                          <button 
                            onClick={() => setIsModalOpen(true)}
                            className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                            aria-label="Show calculator key"
                          >
                            <InformationCircleIcon className="h-7 w-7" />
                          </button>
                          <div className="overflow-x-auto text-xs">
                            <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                            </div>
                            <div className="divide-y divide-gray-700 text-gray-300">
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Units:</span> <span className="font-semibold">13</span></div>
                                <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 13 = 2145</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 13 = 2600</span></div>
                                <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$90</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">3,120,000 (1200/night)</span></div>
                                <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$193,050</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                                <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$77,220</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$3,120,000</span></div>
                                <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$118,000</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$740,000</span></div>
                                <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$195,220</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                                <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$189,800</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Reserve Fund (318.9%):</span> <span className="font-semibold">$2,360,000</span></div>
                                <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$5,420</span></div>
                              </div>
                            </div>
                          </div>
                          <details className="mt-4">
                            <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                              How the numbers are calculated
                            </summary>
                            <div style={{ 
                              backgroundColor: '#111827', 
                              padding: '16px', 
                              borderRadius: '6px', 
                              border: '1px solid #374151',
                              marginTop: '8px'
                            }}>
                              <p className="text-gray-400 text-sm mb-4">
                                The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                              </p>
                              <p className="text-gray-400 text-sm mb-4">
                                Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>



                      {/* Additional Example 4 - Hotel Cusco's Plaza de Armas */}
                      <div>
                        <img 
                          src="/images/Properties/Cuzco Plaza Hotel.jpg" 
                          alt="Hotel Cusco's Plaza de Armas Property" 
                          className="w-full rounded-lg mb-6"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                        />
                        
                        <h4 className="text-2xl font-bold text-gray-300 mb-2">Cusco, Peru — 16-Room Hotel</h4>
                        <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                          Hotel Cusco's Plaza de Armas<br />
                          Saphi Street, Cercado, Cusco<br />
                          16 room hotel, $950,000<br />
                          <a href="https://www.adondevivir.com/propiedades/clasificado/veclhtin-hotel-2-en-venta-a-dos-cuadras-de-la-plaza-de-armas-90209532.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                        </div>
                        
                        <div style={{ 
                          backgroundColor: '#1f2937', 
                          padding: '16px', 
                          borderRadius: '8px', 
                          border: '1px solid #4b5563',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          marginBottom: '20px',
                          position: 'relative'
                        }}>
                          <button 
                            onClick={() => setIsModalOpen(true)}
                            className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                            aria-label="Show calculator key"
                          >
                            <InformationCircleIcon className="h-7 w-7" />
                          </button>
                          <div className="overflow-x-auto text-xs">
                            <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                            </div>
                            <div className="divide-y divide-gray-700 text-gray-300">
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Units:</span> <span className="font-semibold">16</span></div>
                                <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 16 = 2640</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 16 = 3200</span></div>
                                <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$80</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">1,440,000 (450/night)</span></div>
                                <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$211,200</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                                <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$84,480</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$1,440,000</span></div>
                                <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$23,500</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$950,000</span></div>
                                <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$107,980</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                                <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$105,120</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Reserve Fund (49.5%):</span> <span className="font-semibold">$470,000</span></div>
                                <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$2,860</span></div>
                              </div>
                            </div>
                          </div>
                          <details className="mt-4">
                            <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                              How the numbers are calculated
                            </summary>
                            <div style={{ 
                              backgroundColor: '#111827', 
                              padding: '16px', 
                              borderRadius: '6px', 
                              border: '1px solid #374151',
                              marginTop: '8px'
                            }}>
                              <p className="text-gray-400 text-sm mb-4">
                                The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                              </p>
                              <p className="text-gray-400 text-sm mb-4">
                                Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>

                      {/* Long Lake MI Example */}
                      <div>
                        <img 
                          src="/images/4998-Main-Ext-Long-Lake-MI-Building-Photo-2-LargeHighDefinition.jpg" 
                          alt="Long Lake MI Property" 
                          className="w-full rounded-lg mb-6"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                        />
                        
                        <h4 className="text-2xl font-bold text-gray-300 mb-2">Long Lake, MI — 7 Room Hotel</h4>
                        <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                          Long Lake Lodge<br />
                          4998 Main Ext, Long Lake, MI 48743<br />
                          $770,000<br />
                          <a href="https://www.loopnet.com/Listing/4998-Main-Ext-Long-Lake-MI/31972906/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a>
                        </div>
                        
                        <div style={{ 
                          backgroundColor: '#1f2937', 
                          padding: '16px', 
                          borderRadius: '8px', 
                          border: '1px solid #4b5563',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          marginBottom: '20px',
                          position: 'relative'
                        }}>
                          <button 
                            onClick={() => setIsModalOpen(true)}
                            className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                            aria-label="Show calculator key"
                          >
                            <InformationCircleIcon className="h-7 w-7" />
                          </button>
                          <div className="overflow-x-auto text-xs">
                            <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                            </div>
                            <div className="divide-y divide-gray-700 text-gray-300">
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Units:</span> <span className="font-semibold">7</span></div>
                                <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 7 = 1155</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 7 = 1400</span></div>
                                <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$125</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">1,680,000 (1200/night)</span></div>
                                <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$144,375</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                                <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$57,750</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$1,680,000</span></div>
                                <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$44,500</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$770,000</span></div>
                                <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$102,250</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                                <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$102,200</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Reserve Fund (115.6%):</span> <span className="font-semibold">$890,000</span></div>
                                <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$50</span></div>
                              </div>
                            </div>
                          </div>
                          <details className="mt-4">
                            <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                              How the numbers are calculated
                            </summary>
                            <div style={{ 
                              backgroundColor: '#111827', 
                              padding: '16px', 
                              borderRadius: '6px', 
                              border: '1px solid #374151',
                              marginTop: '8px'
                            }}>
                              <p className="text-gray-400 text-sm mb-4">
                                The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                              </p>
                              <p className="text-gray-400 text-sm mb-4">
                                Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>

                      {/* Mancora Peru Example */}
                      <div>
                        <img 
                          src="/images/mancura-1.webp" 
                          alt="Mancora Peru Property" 
                          className="w-full rounded-lg mb-6"
                          style={{ maxHeight: '400px', objectFit: 'cover' }}
                        />
                        
                        <h4 className="text-2xl font-bold text-gray-300 mb-2">Máncora, Peru — Ocean Front Hotel</h4>
                        <div className="text-gray-300 mb-8" style={{ lineHeight: '1.25' }}>
                          Ocean View Hotel<br />
                          Los Incas Street<br />
                          6 Rooms, $170,000<br />
                          <a href="https://www.adondevivir.com/propiedades/clasificado/veclhtin-oportunidad-hotel-en-venta-en-mancora-y-totalmente-145416929.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">See Listing</a><br />
                          <a href="https://www.tripadvisor.com/Hotel_Review-g635976-d1771565-Reviews-Kon_Tiki_Bungalows-Mancora_Piura_Region.html" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Reviews</a>
                        </div>
                        
                        <div style={{ 
                          backgroundColor: '#1f2937', 
                          padding: '16px', 
                          borderRadius: '8px', 
                          border: '1px solid #4b5563',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                          marginBottom: '20px',
                          position: 'relative'
                        }}>
                          <button 
                            onClick={() => setIsModalOpen(true)}
                            className="absolute top-2 right-2 text-blue-300 hover:text-white transition-colors"
                            aria-label="Show calculator key"
                          >
                            <InformationCircleIcon className="h-7 w-7" />
                          </button>
                          <div className="overflow-x-auto text-xs">
                            <div className="grid grid-cols-2 gap-x-4 pb-1 border-b-2 border-gray-500">
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Property Details</div>
                              <div className="text-left py-2 px-2 font-semibold text-blue-300">Annual DAO Income</div>
                            </div>
                            <div className="divide-y divide-gray-700 text-gray-300">
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Units:</span> <span className="font-semibold">6</span></div>
                                <div><span className="text-gray-500">DAO Nights:</span> <span className="font-semibold">165 × 6 = 990</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Nights for Members:</span> <span className="font-semibold">200 × 6 = 1200</span></div>
                                <div><span className="text-gray-500">Price per Night:</span> <span className="font-semibold">$20</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credits Sold:</span> <span className="font-semibold">480,000 (400/night)</span></div>
                                <div><span className="text-gray-500">Gross Potential:</span> <span className="font-semibold">$19,800</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Credit Price:</span> <span className="font-semibold">$1.00</span></div>
                                <div><span className="text-gray-500">40% Occupancy:</span> <span className="font-semibold">$7,920</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Total Raised:</span> <span className="font-semibold">$480,000</span></div>
                                <div><span className="text-gray-500">Reserve Yield (5%):</span> <span className="font-semibold">$14,500</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Purchase Price:</span> <span className="font-semibold">$170,000</span></div>
                                <div><span className="text-gray-500">Total Coverage:</span> <span className="font-bold">$22,420</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Legal/Admin:</span> <span className="font-semibold">$20,000</span></div>
                                <div><span className="text-gray-500">Estimated Ops Cost:</span> <span className="font-semibold">$21,900</span></div>
                              </div>
                              <div className="grid grid-cols-2 gap-x-4 py-1 px-2">
                                <div><span className="text-gray-500">Reserve Fund (170.6%):</span> <span className="font-semibold">$290,000</span></div>
                                <div><span className="text-gray-500">Surplus:</span> <span className="text-green-400 font-bold">~$520</span></div>
                              </div>
                            </div>
                          </div>
                          <details className="mt-4">
                            <summary className="text-blue-300 cursor-pointer hover:text-blue-200 text-sm">
                              How the numbers are calculated
                            </summary>
                            <div style={{ 
                              backgroundColor: '#111827', 
                              padding: '16px', 
                              borderRadius: '6px', 
                              border: '1px solid #374151',
                              marginTop: '8px'
                            }}>
                              <p className="text-gray-400 text-sm mb-4">
                                The calculator automatically determines the required "credits per night" to make each project financially sustainable. It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, legal/admin fees, and a minimum reserve—while also generating a positive projected surplus. Surplus is based on DAO income (40% of gross potential at the given rate), reserve yield, and estimated annual operating costs.
                              </p>
                              <p className="text-gray-400 text-sm mb-4">
                                Operating costs default to $20 per day but can be adjusted to reflect local conditions, property type, and amenities. Actual costs are often lower thanks to automated check-ins via Key-linked QR codes and efficiencies from AI-enabled systems.
                              </p>
                            </div>
                          </details>
                        </div>
                      </div>

                    </div>
                  )}


                </div>
              </div>
            </div>

          {/* Right Column - Innovation */}
          <div className="space-y-6" style={{ paddingLeft: '32px', paddingRight: '80px', paddingTop: '32px', paddingBottom: '32px', lineHeight: '1.5' }}>
            <h2 className="text-4xl font-bold mb-8" style={{ color: '#36a9f7' }}>Innovation</h2>
            
            <div className="text-gray-400 space-y-4 leading-relaxed mb-8">
              <p>
                BornFree runs on smart contracts designed to encode fairness, transparency, and adaptability for every community member. Below are the automated systems that drive our ecosystem:
              </p>
            </div>

            {/* Immutable Ledgers + AI Section */}
            <div className="mb-12">
              <h3 className="text-2xl font-bold mb-6" style={{ color: '#60a5fa' }}>Immutable Ledgers + AI</h3>
              
              <div className="text-gray-400 space-y-4 leading-relaxed">
                <p>
                  We chose the <a href="https://www.avax.network/" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">Avalanche</a> because it offers low-cost, eco-friendly transactions, near instant finality and high throughput via a mature and secure Layer 1 ecosystem.
                </p>
                
                <p className="pt-4">
                  <a href="https://arweave.org" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">Arweave</a> and <a href="https://pinata.cloud" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">Pinata</a> are used for permanent and accessible on chain data. <a href="https://internetcomputer.org" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">Internet Computer</a> canisters provide decentralized lightning speed service, replacing typical centralized solutions. Hosting is decentralized on chain using IPFS as well.
                </p>
                
                <p className="pt-4">
                  To efficiently manage blockchain data and support smart automation, we use <a href="https://thegraph.com" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">The Graph</a> — a decentralized indexing protocol — to parse on-chain data in real time. This allows our AI agents to analyze live member activity, credit usage, and booking patterns while preserving user privacy.
                </p>
                
                <p className="pt-2">
                  These insights help us:
                </p>
                
                <ul className="list-disc ml-6 space-y-2">
                  <li>Optimize credit flows and reservation availability</li>
                  <li>Predict regional demand across locations</li>
                  <li>Automate DAO treasury balancing and credit renewal logic</li>
                </ul>
                
                <p className="pt-4">
                  By integrating blockchain and AI, we're unlocking a system that's not just decentralized — but <strong style={{ color: '#60a5fa' }}>intelligent</strong>. It adapts with the community, learns from behavior, and evolves to better serve our shared mission.
                </p>
                
                <p className="pt-4">
                  We're building more than just a platform — we're co-creating a <strong style={{ color: '#60a5fa' }}>living system of cooperation</strong>, powered by unstoppable tech and human alignment.
                </p>
              </div>
            </div>
            
            {/* Cutting Edge */}
            <div className="mb-12" style={{ paddingTop: '3rem' }}>
              <h3 className="text-2xl font-bold mb-6 text-gray-300" style={{ color: '#36a9f7' }}>Cutting Edge</h3>
              <div className="text-gray-400 space-y-4 leading-relaxed">
                <p>
                  BornFree brings together MCP-adapted features and a custom LLM chatbot with RAG (retrieval-augmented generation), creating a future-facing stack that connects seamlessly with other apps and agents. Members get instant lookup of platform information—reservations, credits, marketplace listings, DAO activity, docNFTs, tags, categories, dates, and more—without leaving the flow of their work.
                </p>
                <h4 className="text-gray-300 font-semibold mt-4 mb-2" style={{ color: '#60a5fa' }}>Member ID privacy</h4>
                <p>
                  We treat Member NFT IDs and on-chain identity as private by design. Members decide what to share; ownership and activity stay under their control, and we do not expose identity beyond what the member or the system requires for reservations and governance. This is part of our broader use of decentralized privacy tech across the stack.
                </p>
                <h4 className="text-gray-300 font-semibold mt-4 mb-2" style={{ color: '#60a5fa' }}>Zero Access Encryption</h4>
                <p>
                  Combined with zero-access encryption and VetKey-secured architecture, the entire pipeline—from document signing and Key operations to NFT minting and governance—runs in an ultra-secure environment built for the next decade of Web3.
                </p>
                <ul className="list-disc ml-6 space-y-2">
                  <li>Connect with other apps and agents via MCP</li>
                  <li>Instant lookup: reservations, credits, marketplace, DAO activity, docNFTs, tags, categories, dates</li>
                  <li>Custom LLM chatbot with RAG for accurate, context-aware answers</li>
                  <li>Member ID and NFT privacy; decentralized identity</li>
                  <li>Zero-access encryption and secure key management</li>
                </ul>
              </div>
            </div>
            
            {/* Logic Section */}
            <div className="mb-12" style={{ paddingTop: '3rem' }}>
              <h2 className="text-4xl font-bold mb-8 text-gray-300">Logic</h2>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {/* Reservation Key */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">🏕️</span>
                  Reservation Key
                </h3>
                <p className="text-gray-400 mb-4">
                  Reservation Keys represent on-chain bookings for community spaces, retreats, or events. Each Key is made using credits from an ExperienceKey and tied to a specific date and location.
                </p>
                
                <div style={{ marginTop: '24px' }}>
                  <p className="text-gray-300 font-semibold" style={{ marginBottom: '12px' }}>Smart Contract Structure:</p>
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '24px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <pre style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '14px', 
                        color: '#d1d5db', 
                        margin: 0,
                        overflowX: 'auto',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        <code>{`struct Reservation {
    uint256 date;
    address user;
    string location;
    bytes32 qrHash; // On-chain hash of QR string
    bool confirmed;
}

mapping(uint256 => bool) public isDateReserved;
mapping(address => Reservation[]) public reservations;

function mintReservationKey(
    uint256 selectedDate,
    string calldata location,
    bytes32 qrHash,
    uint256 creditCost
) external {
    require(block.timestamp >= reservationStart && block.timestamp <= reservationEnd, "Outside reservation window");
    require(selectedDate >= block.timestamp + 30 days, "Must reserve 30+ days in advance");
    require(!isDateReserved[selectedDate], "Date already reserved");
    
    burnCredits(msg.sender, creditCost);
    isDateReserved[selectedDate] = true;
    
    reservations[msg.sender].push(Reservation({
        date: selectedDate,
        user: msg.sender,
        location: location,
        qrHash: qrHash,
        confirmed: false
    }));
    
    emit ReservationCreated(msg.sender, selectedDate, location);
}`}</code>
                      </pre>
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        fontSize: '12px',
                        color: '#9ca3af',
                        backgroundColor: 'rgba(31, 41, 55, 0.8)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #4b5563'
                      }}>
                        ↕ Scroll to see more
                      </div>
                    </div>
                  </div>
                </div>

                {/* Variable Reference Dropdown */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    Variable Reference
                  </summary>
                  <div style={{ 
                    backgroundColor: '#111827', 
                    padding: '16px', 
                    borderRadius: '6px', 
                    border: '1px solid #374151',
                    marginTop: '8px'
                  }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/4">Variable / Field</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/6">Type</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">selectedDate</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">The reservation date (as a Unix timestamp); must be 30+ days in the future</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">location</td>
                            <td className="py-3 px-4 text-yellow-300">string calldata</td>
                            <td className="py-3 px-4">The name or ID of the property, unit, or location being reserved</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">qrHash</td>
                            <td className="py-3 px-4 text-yellow-300">bytes32</td>
                            <td className="py-3 px-4">On-chain hash of the QR code string (e.g., keccak256(qrString)) for check-in</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">creditCost</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Number of credits required to mint this ReservationKey</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">reservationStart</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Global start of the reservation season (e.g., June 1, Unix time)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">reservationEnd</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Global end of the reservation season (e.g., Nov 30, Unix time)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">isDateReserved</td>
                            <td className="py-3 px-4 text-yellow-300">mapping(uint256 =&gt; bool)</td>
                            <td className="py-3 px-4">Keeps track of reserved dates to prevent duplicate bookings</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">reservations</td>
                            <td className="py-3 px-4 text-yellow-300">mapping(address =&gt; Reservation[])</td>
                            <td className="py-3 px-4">Stores reservations per user</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">Reservation</td>
                            <td className="py-3 px-4 text-yellow-300">struct</td>
                            <td className="py-3 px-4">Holds all key reservation data (date, user, location, QR hash, etc.)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">confirmed</td>
                            <td className="py-3 px-4 text-yellow-300">bool</td>
                            <td className="py-3 px-4">Boolean flag for confirming a reservation (could be used later for check-in)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">burnCredits(...)</td>
                            <td className="py-3 px-4 text-yellow-300">function</td>
                            <td className="py-3 px-4">Deducts credits from user's ExperienceKey credit balance</td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 font-mono text-blue-300">emit ReservationCreated(...)</td>
                            <td className="py-3 px-4 text-yellow-300">event</td>
                            <td className="py-3 px-4">Emits log to track and sync frontend/calendar systems</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
              </div>

              <hr className="border-gray-600" />

              {/* Round Robin Basic Formula */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">🎯</span>
                  Round Robin Basic Formula
                </h3>
                <p className="text-gray-400 mb-4">
                  Basic queue position assignment without merging, based on the timestamp of when the key was made.
                </p>
                
                <div style={{ marginTop: '24px' }}>
                  <p className="text-gray-300 font-semibold" style={{ marginBottom: '12px' }}>Basic position formula:</p>
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '24px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px'
                  }}>
                    <pre style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px', 
                      color: '#d1d5db', 
                      margin: 0,
                      overflowX: 'auto'
                    }}>
                      <code>{`queuePosition = (tierLevel * 1000) + mintTimestamp;`}</code>
                    </pre>
                  </div>
                </div>

                <p className="text-gray-400 mb-4">
                  Lower queuePosition = higher priority in line
                </p>

                {/* Variable Reference Dropdown */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    Variable Reference
                  </summary>
                  <div style={{ 
                    backgroundColor: '#111827', 
                    padding: '16px', 
                    borderRadius: '6px', 
                    border: '1px solid #374151',
                    marginTop: '8px'
                  }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/4">Variable</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/6">Type</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">tierLevel</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Numeric representation of ExperienceKey tier (e.g., 1 for 2000, 2 for 1750, etc.) — lower tierLevel = better priority</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">mintTimestamp</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">The Unix timestamp (in seconds) when the Key was minted</td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 font-mono text-blue-300">queuePosition</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Calculated value used to sort users for fair round-robin access; <strong>lower is better</strong></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
              </div>

              <hr className="border-gray-600" />

              {/* 8-Year Round Robin Rotation Cycle */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">🎯</span>
                  8-Year Round Robin Rotation Cycle
                </h3>
                
                {/* Important: When RR Applies */}
                <div className="mb-4 p-3 bg-blue-900 bg-opacity-30 border border-blue-500 rounded-lg">
                  <p className="text-blue-200 text-sm m-0">
                    <strong>📅 Applies During Reservation Season Only:</strong> Round Robin priority is used <strong>June 1-30</strong> when making Reservation Keys. 
                    Outside this window, credits are <strong>first-come-first-serve</strong>.
                  </p>
                </div>

                <p className="text-gray-400 mb-4">
                  Every Experience Key gets a permanent <strong className="text-blue-300">Original RR#</strong> based on mint order. 
                  Your <strong className="text-green-300">Effective RR#</strong> advances <strong>clockwise each year</strong> by a variable amount (typically 201-601 positions depending on project size), 
                  completing an 8-year cycle. Everyone reaches Position #1 within 8 years, ensuring lifetime fairness.
                </p>

                {/* Diagram */}
                <div className="mb-6 flex justify-center">
                  <RoundRobinCycleDiagram size={350} showLabels={true} />
                </div>

                {/* How It Works - Now Collapsible */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2 text-base">
                    ▶ How It Works
                  </summary>
                  <div className="mt-3 p-4 bg-gray-800 border border-gray-600 rounded-lg">
                    <ul className="text-gray-400 space-y-2 text-sm">
                      <li><strong className="text-green-300">Year 1:</strong> You start at your Original RR# (e.g., #1)</li>
                      <li><strong className="text-blue-300">Year 2:</strong> Position advances 201 spots (e.g., #1 → #202)</li>
                      <li><strong className="text-purple-300">Years 3-7:</strong> Continues advancing 201 spots per year</li>
                      <li><strong className="text-yellow-300">Year 8:</strong> Reaches #1408, then cycles back to Position #9</li>
                      <li><strong className="text-orange-300">Cycle Repeats:</strong> The 8-year cycle continues for the lifetime of the project</li>
                    </ul>
                  </div>
                </details>

                {/* Scaling Table */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    📊 Scales with Any Project Size
                  </summary>
                  <div style={{ 
                    backgroundColor: '#111827', 
                    padding: '16px', 
                    borderRadius: '6px', 
                    border: '1px solid #374151',
                    marginTop: '8px'
                  }}>
                    <p className="text-gray-300 mb-3 text-sm">
                      Formula: <code className="text-blue-300 bg-gray-700 px-2 py-1 rounded">(maxSupply ÷ 8) + 1 = positions/year</code>
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Project Size</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Max Supply</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Positions/Year</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">8-Year Cycle</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr className="border-b border-gray-700 bg-blue-900 bg-opacity-20">
                            <td className="py-3 px-4"><strong>Genesis (Current)</strong></td>
                            <td className="py-3 px-4">1,600 nights</td>
                            <td className="py-3 px-4">201 (200 + 1)</td>
                            <td className="py-3 px-4 text-green-300">✅ Complete cycle</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4">Small Project</td>
                            <td className="py-3 px-4">800 nights</td>
                            <td className="py-3 px-4">101 (100 + 1)</td>
                            <td className="py-3 px-4 text-green-300">✅ Complete cycle</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4">Large Project</td>
                            <td className="py-3 px-4">3,200 nights</td>
                            <td className="py-3 px-4">401 (400 + 1)</td>
                            <td className="py-3 px-4 text-green-300">✅ Complete cycle</td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4">Mega Project</td>
                            <td className="py-3 px-4">4,800 nights</td>
                            <td className="py-3 px-4">601 (600 + 1)</td>
                            <td className="py-3 px-4 text-green-300">✅ Complete cycle</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-gray-400 text-xs mt-3 italic">
                      The "+1" ensures progressive shift so positions never repeat exactly, maintaining fairness across all cycles.
                    </p>
                  </div>
                </details>

                {/* Original vs Effective RR# - Now Collapsible */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    📌 Original RR# vs ⚡ Effective RR#
                  </summary>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-800 border-2 border-blue-500 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-300 mb-2">
                        📌 Original RR#
                      </h4>
                      <p className="text-gray-400 text-sm mb-2">
                        Stored in your NFT metadata, never changes
                      </p>
                      <ul className="text-gray-400 text-sm space-y-1 list-disc list-inside">
                        <li>Based on mint order</li>
                        <li>Permanent & immutable</li>
                        <li>Your "starting point"</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-gray-800 border-2 border-green-500 rounded-lg">
                      <h4 className="text-md font-semibold text-gray-300 mb-2">
                        ⚡ Effective RR#
                      </h4>
                      <p className="text-gray-400 text-sm mb-2">
                        Calculated by Rotation Manager, changes annually
                      </p>
                      <ul className="text-gray-400 text-sm space-y-1 list-disc list-inside">
                        <li>Used for reservations</li>
                        <li>Advances each year</li>
                        <li>Your "current priority"</li>
                      </ul>
                    </div>
                  </div>
                </details>
              </div>

              <hr className="border-gray-600" />

              {/* Annual Credit Timeline */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">📅</span>
                  Annual Credit Timeline
                </h3>
                <p className="text-gray-400 mb-4">
                  Automatically resets credits, defines usage window, and cleans up unused tokens.
                </p>
                
                <div style={{ marginTop: '24px' }}>
                  <p className="text-gray-300 font-semibold" style={{ marginBottom: '12px' }}>Smart contract logic:</p>
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '24px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px'
                  }}>
                    <div style={{ position: 'relative' }}>
                      <pre style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '14px', 
                        color: '#d1d5db', 
                        margin: 0,
                        overflowX: 'auto',
                        maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        <code>{`if (
  block.timestamp > usageYearEnd &&
  block.timestamp > eventDate - 30 days
) {
  // Transfer expired credits to DAO instead of burning
  safeTransferFrom(user, daoAddress, creditTokenId, balanceOf(user, creditTokenId), "");
  emit CreditReclaimed(user, creditTokenId, balance, "expired");
}

if (
  block.timestamp >= eventDate - 30 days &&
  !isReserved(eventDate)
) {
  dao.mintReservationKey(eventDate);
  dao.listOnMarketplace(eventDate);
}`}</code>
                      </pre>
                      <div style={{
                        position: 'absolute',
                        bottom: '8px',
                        right: '8px',
                        fontSize: '12px',
                        color: '#9ca3af',
                        backgroundColor: 'rgba(31, 41, 55, 0.8)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        border: '1px solid #4b5563'
                      }}>
                        ↕ Scroll to see more
                      </div>
                    </div>
                  </div>
                </div>

                {/* Variable Reference Dropdown */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    Variable Reference
                  </summary>
                  <div style={{ 
                    backgroundColor: '#111827', 
                    padding: '16px', 
                    borderRadius: '6px', 
                    border: '1px solid #374151',
                    marginTop: '8px'
                  }}>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-600">
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/4">Variable</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold w-1/6">Type</th>
                            <th className="text-left py-3 px-4 text-gray-300 font-semibold">Description</th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-300">
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">block.timestamp</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Current block time in seconds (EVM standard)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">usageYearEnd</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Final cutoff timestamp for using credits (Nov 30 of usage year)</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">eventDate</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">The date of the event or experience the credit could be used for</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">creditTokenId</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">The ID of the ERC-1155 credit token being tracked</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">user</td>
                            <td className="py-3 px-4 text-yellow-300">address</td>
                            <td className="py-3 px-4">The wallet address holding the expiring credit</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">daoAddress</td>
                            <td className="py-3 px-4 text-yellow-300">address</td>
                            <td className="py-3 px-4">DAO wallet address that receives reclaimed credits</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">balance</td>
                            <td className="py-3 px-4 text-yellow-300">uint256</td>
                            <td className="py-3 px-4">Number of credits held by user for creditTokenId</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">isReserved(date)</td>
                            <td className="py-3 px-4 text-yellow-300">function</td>
                            <td className="py-3 px-4">Returns true if a ReservationKey has already been minted for eventDate</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">dao.mintReservationKey(date)</td>
                            <td className="py-3 px-4 text-yellow-300">function</td>
                            <td className="py-3 px-4">Mints a ReservationKey for DAO-held availability</td>
                          </tr>
                          <tr className="border-b border-gray-700">
                            <td className="py-3 px-4 font-mono text-blue-300">dao.listOnMarketplace(date)</td>
                            <td className="py-3 px-4 text-yellow-300">function</td>
                            <td className="py-3 px-4">DAO lists the open slot on secondary or internal market</td>
                          </tr>
                          <tr>
                            <td className="py-3 px-4 font-mono text-blue-300">emit CreditReclaimed(...)</td>
                            <td className="py-3 px-4 text-yellow-300">event</td>
                            <td className="py-3 px-4">Optional event for logging DAO takeover of unused credits</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </details>
                
                <ul className="text-gray-400 space-y-1 pl-5 list-disc">
                  <li>Jan 1: Credits replenished based on initial purchase tier</li>
                  <li>Jan 8 – May 31: Buy, sell, trade, and merge credits</li>
                  <li>June 1 – June 30: Make Reservation Key season</li>
                  <li>June 30: Round Robin reservations end. Credits all equal.</li>
                  <li>First come first serve.</li>
                  <li>Dec 31: Last day of usage year, all credits burned</li>
                </ul>
              </div>

              <hr className="border-gray-600" />

              {/* Credit Merging Logic */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">🧬</span>
                  Credit Merging Logic
                </h3>
                <p className="text-gray-400 mb-4">
                  When you merge credits from multiple Keys with different Round Robin numbers:
                </p>
                <ul className="list-disc list-inside text-gray-400 mb-4 space-y-2">
                  <li>The system first matches the lower quantity of credits from both Keys and merges them 1-to-1 using a weighted average.</li>
                  <li>If one Key has extra credits, those are averaged into the new total more gradually, based on the existing merged average.</li>
                </ul>
                <p className="text-gray-400 mb-6">
                  This method prevents manipulation while still honoring the full value of all credits over time.
                </p>

                {/* Formula with Surplus Averaging */}
                <div className="mb-8">
                  <h4 className="text-md font-semibold text-gray-300 mb-3">Formula with Surplus Averaging</h4>
                  <p className="text-gray-400 mb-2">Let's define:</p>
                  <ul className="list-disc list-inside text-gray-400 mb-4 space-y-1">
                    <li><span className="font-mono text-blue-300">baseRR</span> = Round Robin # of the original Key</li>
                    <li><span className="font-mono text-blue-300">baseCredits</span> = how many credits in that Key</li>
                    <li><span className="font-mono text-blue-300">incomingRR</span> = RR# of the new credits being merged</li>
                    <li><span className="font-mono text-blue-300">incomingCredits</span> = how many new credits are being merged</li>
                  </ul>
                  <p className="text-gray-400 mb-3">We separate the merge into two stages:</p>
                  
                  <details className="mb-4">
                    <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-bold mb-2">
                      Two stages & example
                    </summary>
                    <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '6px', border: '1px solid #374151', marginTop: '8px' }}>
                      <div className="mb-4">
                        <p className="text-gray-300 font-semibold mb-2">1. One-to-One Merge Portion</p>
                        <p className="text-gray-400 text-sm mb-2">If the incoming credits are fewer or equal to the original:</p>
                        <div style={{ backgroundColor: '#1f2937', padding: '16px', borderRadius: '6px', border: '1px solid #4b5563' }}>
                          <pre className="font-mono text-sm text-gray-300" style={{ overflowX: 'auto' }}><code>{`weightedRR = (baseRR * baseCredits + incomingRR * incomingCredits) / (baseCredits + incomingCredits)`}</code></pre>
                        </div>
                      </div>

                      <div>
                        <p className="text-gray-300 font-semibold mb-2">2. If Incoming {'>'} Base: Add Surplus</p>
                        <p className="text-gray-400 text-sm mb-2">Let:</p>
                        <div style={{ backgroundColor: '#1f2937', padding: '16px', borderRadius: '6px', border: '1px solid #4b5563', marginBottom: '8px' }}>
                          <pre className="font-mono text-sm text-gray-300" style={{ overflowX: 'auto' }}><code>{`equalMerge = baseCredits;\nsurplus = incomingCredits - baseCredits;`}</code></pre>
                        </div>
                        <p className="text-gray-400 text-sm mb-2">Then:</p>
                        <div style={{ backgroundColor: '#1f2937', padding: '16px', borderRadius: '6px', border: '1px solid #4b5563' }}>
                          <pre className="font-mono text-sm text-gray-300" style={{ overflowX: 'auto' }}><code>{`intermediateRR = (baseRR * baseCredits + incomingRR * equalMerge) / (baseCredits + equalMerge);\nfinalRR = (intermediateRR * (baseCredits + equalMerge) + incomingRR * surplus) / (baseCredits + incomingCredits);`}</code></pre>
                        </div>
                      </div>

                      <div className="mt-6">
                        <h4 className="text-md font-semibold text-gray-300 mb-3">Example:</h4>
                        <p className="text-gray-400 mb-2">You have:</p>
                        <ul className="list-disc list-inside text-gray-400 mb-4 space-y-1">
                          <li>Key A: 100 credits at RR₁ = 1</li>
                          <li>Key B: 200 credits at RR₂ = 5</li>
                        </ul>

                        <div className="mb-4">
                          <p className="text-gray-300 font-semibold mb-2">Step 1: 1-to-1 Merge of 100 each</p>
                          <div style={{ backgroundColor: '#1f2937', padding: '16px', borderRadius: '6px', border: '1px solid #4b5563' }}>
                            <pre className="font-mono text-sm text-gray-300 overflow-x-auto"><code>{`RRavg_initial = (1*100 + 5*100) / 200 = 3.0`}</code></pre>
                          </div>
                        </div>

                        <div className="mb-4">
                          <p className="text-gray-300 font-semibold mb-2">Step 2: Merge remaining 100 credits at RR = 5 into 3.0</p>
                          <div style={{ backgroundColor: '#1f2937', padding: '16px', borderRadius: '6px', border: '1px solid #4b5563' }}>
                            <pre className="font-mono text-sm text-gray-300 overflow-x-auto"><code>{`RR_final = (3.0*200 + 5*100) / 300 = (600 + 500)/300 = 3.67`}</code></pre>
                          </div>
                        </div>
                        <p className="text-gray-300 font-semibold">So your new Round Robin number = 3.67</p>
                      </div>
                    </div>
                  </details>

                </div>

                {/* Variable Reference Dropdown */}
                <details className="mb-4">
                  <summary className="text-blue-400 cursor-pointer hover:text-blue-300 font-medium mb-2">
                    Variable Reference
                  </summary>
                  <div style={{ backgroundColor: '#111827', padding: '16px', borderRadius: '6px', border: '1px solid #374151', marginTop: '8px' }}>
                    <p className="text-gray-300 font-semibold mb-2">Variables:</p>
                    <ul className="list-disc list-inside text-gray-400 mb-4 space-y-1">
                      <li>RR₁: Round Robin Number of original Key</li>
                      <li>C₁: Number of credits in original Key</li>
                      <li>RR₂: Round Robin Number of merging Key</li>
                      <li>C₂: Number of credits in merging Key</li>
                    </ul>
                    <p className="text-gray-300 font-semibold mb-2">Let:</p>
                    <ul className="list-disc list-inside text-gray-400 space-y-2 font-mono">
                      <li>M = min(C₁, C₂) → The matched part (1-to-1 merged)</li>
                      <li>S = abs(C₁ - C₂) → The surplus beyond the matched merge</li>
                      <li>RRavg_initial = (RR₁ * M + RR₂ * M) / (2 * M)</li>
                      <li>RR_final = (RRavg_initial * 2M + RR₂ * S) / (2M + S)</li>
                    </ul>
                  </div>
                </details>
              </div>

              <hr className="border-gray-600" />

              {/* Governance */}
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-300 flex items-center">
                  <span className="mr-2">🗳️</span>
                  Governance: Credit-Weighted Voting
                </h3>
                <p className="text-gray-400 mb-4">
                  Voting power = current year's active credit balance.
                </p>
                
                <div style={{ marginTop: '24px' }}>
                  <p className="text-gray-300 font-semibold" style={{ marginBottom: '12px' }}>Voting weight formula:</p>
                  <div style={{ 
                    backgroundColor: '#1f2937', 
                    padding: '24px', 
                    borderRadius: '8px', 
                    border: '1px solid #4b5563',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    marginBottom: '20px'
                  }}>
                    <pre style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px', 
                      color: '#d1d5db', 
                      margin: 0,
                      overflowX: 'auto'
                    }}>
                      <code>{`votingPower = sum(user.credits[tokenId] for all tokenIds);`}</code>
                    </pre>
                  </div>
                </div>
                
                <div>
                  <p className="text-gray-300 font-semibold mb-2">Used to vote on:</p>
                  <ul className="text-gray-400 space-y-1">
                    <li>• DAO credit policy</li>
                    <li>• Experience or project approval</li>
                    <li>• Community upgrades</li>
                  </ul>
                    </div>
                  </div>
                  
            </div>

            {/* Tools — full plain-language copy; technical depth in collapsible details */}
            <div className="mb-10" style={{ paddingTop: '2rem' }}>
              <h2 className="text-4xl font-bold mb-6" style={{ color: '#36a9f7' }}>Tools</h2>
              <div className="text-gray-400 space-y-6 leading-relaxed">
                <p>
                  These tools were originally created to support the decentralized activities of BornFree communities. We really liked what was created and thought we should share these powerful, future-facing apps so anyone can use them for free.
                </p>

                <div className="space-y-3">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#36a9f7' }}>StoreFree</h3>
                  <p className="text-gray-300">
                    <strong>StoreFree</strong> is for the material that actually matters: <strong>documents, images, video, audio</strong>, and the rest of your personal and professional records—not just “files in a folder.” You get real <strong>signing workflows</strong> (like the tools enterprises use), <strong>encrypted storage</strong>, and optional <strong>DocNFTs</strong> so <strong>ownership and provenance live on-chain</strong>: an immutable, verifiable trail of what you stored and agreed to, instead of a vendor’s opaque database.
                  </p>
                  <p className="text-gray-300">
                    <strong>Permanent copies</strong> can go to <strong>Arweave</strong> with a <strong>one-time</strong> mint—stop paying rent forever for the same terabytes. Compared with typical cloud drive + e-sign stacks, most users see <strong>roughly two-thirds lower ongoing cost</strong> while gaining <strong>true ownership</strong> of high-value artifacts. Sign in with <strong>email</strong> or use <strong>your own wallet</strong>; either way the archive is yours, not ours to resell or lock away.
                  </p>
                  <details className="rounded-md border border-gray-700/80 bg-gray-800/30 px-3 py-2">
                    <summary className="cursor-pointer text-[#60a5fa] font-semibold text-sm list-none [&::-webkit-details-marker]:hidden">
                      Technical details
                    </summary>
                    <p className="mt-3 text-gray-400 text-sm leading-relaxed">
                      Zero-access encryption: encryption and decryption run in your browser; we never hold your master passphrase or plaintext. DocNFTs (on-chain metadata) pair with Arweave permanence for files you mint as permanent records. Posterity supports digital inheritance—subscription-tier options plus stronger, on-chain-forward setups when you use DocNFT permanence (see the technical whitepaper). Custodial minting and signing use IC threshold ECDSA so people on email can still reach the chain without managing keys daily. StoreFree doubles as the BornFree gateway: subscriptions, DocNFT size tiers, member pricing, and credits tie into the same platform.
                    </p>
                  </details>
                </div>

                <div className="space-y-3">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#36a9f7' }}>Knowtation</h3>
                  <p className="text-gray-300">
                    <strong>Knowtation</strong> (know + notation) is your personal vault and knowledge hub: one place for notes, media, and the story of what you know—your data stays yours, not rented back to you inside a single vendor’s walled garden. Hook up AI agents to that same vault so they stay up to date with your context and history across sessions and tools, instead of acting like a new hire on their first day every time you switch apps. You stay portable; no one platform has to own your brain.
                  </p>
                  <details className="rounded-md border border-gray-700/80 bg-gray-800/30 px-3 py-2">
                    <summary className="cursor-pointer text-[#60a5fa] font-semibold text-sm list-none [&::-webkit-details-marker]:hidden">
                      Technical details
                    </summary>
                    <p className="mt-3 text-gray-400 text-sm leading-relaxed">
                      Technically, it is a personal knowledge system for capturing, transcribing, indexing, and searching notes and media in one place—built as an open brain parseable by agents—with a CLI and{' '}
                      <a href="https://github.com/aaronrene/knowtation" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">Skill Manifest</a>
                      {' '}interface so AI tools can integrate without bloated custom definitions. Your source of truth is an Obsidian-style Markdown vault that stays editor-agnostic; vector-backed semantic search and multi-project folders let you scope work by tag or query across everything. Optional capture from Telegram, WhatsApp, and Discord (plus imports from common LLM and doc platforms) keeps one vault searchable for any workflow; optional memory and intent attestation (AIR) hooks support traceable, authorized automation. The project is open source—see{' '}
                      <a href="https://github.com/aaronrene/knowtation" target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', fontWeight: 'bold' }} className="hover:text-blue-300">github.com/aaronrene/knowtation</a>
                      {' '}for specs, setup, and agent orchestration.
                    </p>
                  </details>
                </div>
              </div>
            </div>

          </div>
        </div>
            
      </div>
        {/* Property Calculator Section */}
        <div className="mt-20 bg-gray-800 rounded-lg mx-4 sm:mx-8 md:mx-16 lg:mx-16 px-4 sm:px-8 md:px-12 lg:px-20 py-8 sm:py-12 md:py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20">
            {/* Left Column - Calculator */}
            <div className="space-y-6">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6 lg:mb-8" style={{ color: '#60a5fa' }}>Strength in Numbers</h3>
              
              <PropertyCalculator />
            </div>
            
            {/* Right Column - Invitation Text */}
            <div className="space-y-6">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 lg:mb-8">Find An Experience</h3>
                
              <div className="text-gray-400 space-y-4 leading-relaxed">
                <p>
                  Our platform depends on mass human creativity and intuitive analysis. Please play with the calculator and help plan the next move.
                </p>
                
                <p>
                  Ready to propose a property for the BornFree community? Start your search on these platforms:
                </p>
                
                <ul className="space-y-1">
                  <li>• <a href="https://www.loopnet.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">LoopNet.com</a> - Commercial real estate</li>
                  <li>• <a href="https://www.bizbuysell.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">BizBuySell.com</a> - Business & property sales</li>
                  <li>• <a href="https://www.zillow.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Zillow.com</a> - Residential properties</li>
                  <li>• <a href="https://www.idealista.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Idealista.com</a> - International properties</li>
                  <li>• <a href="https://www.adondevivir.com/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">AdondeVivir.com</a> - Latin America properties</li>
                </ul>
                
                {/* Share Your Findings Box */}
                <div className="bg-gray-700 p-4 sm:p-6 rounded-lg border border-gray-600 shadow-lg mt-8 text-left">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4" style={{ color: '#93c5fd' }}>💬 Share Your Findings</h3>
                  <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4 sm:mb-6">
                    Have you discovered an intriguing property? Share your insights and explore possible Key project ideas with the BornFree community on Discord. Whether you've done your own research or are seeking input, tap into the collective wisdom of the group to uncover potential challenges and opportunities.
                  </p>
                  <a href="https://discord.com/invite/nzU3VPgwpF" target="_blank" rel="noopener noreferrer" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base">
                    Join Discord Community
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Calculator Key Modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setIsModalOpen(false)}
        >
          <div 
            className="bg-gray-800 text-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-8 relative">
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute top-6 right-6 text-gray-400 hover:text-white"
              >
                <XMarkIcon className="h-8 w-8" />
              </button>
            <h2 className="text-3xl font-bold mb-6 text-center text-blue-300">Calculator Key</h2>
            
            <div className="space-y-6 text-gray-300">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Property Details</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold text-blue-300">Units:</span> Number of rentable units in the property</p>
                  <p><span className="font-semibold text-blue-300">Nights for Members:</span> Total available nights per year for DAO members (200 nights × units)</p>
                  <p><span className="font-semibold text-blue-300">Credits Sold:</span> Total credits generated from member nights (nights × credits per night)</p>
                  <p><span className="font-semibold text-blue-300">Credit Price:</span> Fixed at $1.00 per credit</p>
                  <p><span className="font-semibold text-blue-300">Total Raised:</span> Credits sold × credit price</p>
                  <p><span className="font-semibold text-blue-300">Purchase Price:</span> Property acquisition cost</p>
                  <p><span className="font-semibold text-blue-300">Legal/Admin:</span> Standard $20,000 for legal and administrative setup</p>
                  <p><span className="font-semibold text-blue-300">Reserve Fund:</span> Minimum operating capital (calculated as annual operating costs)</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Annual DAO Income</h3>
                <div className="space-y-2 text-sm">
                  <p><span className="font-semibold text-blue-300">DAO Nights:</span> Remaining nights available for public rental (365 - 200) × units</p>
                  <p><span className="font-semibold text-blue-300">Price per Night:</span> Market rate for public rentals</p>
                  <p><span className="font-semibold text-blue-300">Gross Potential:</span> DAO nights × price per night</p>
                  <p><span className="font-semibold text-blue-300">40% Occupancy:</span> Conservative estimate of actual rental income</p>
                  <p><span className="font-semibold text-blue-300">Reserve Yield (5%):</span> Annual return on reserve fund investment</p>
                  <p><span className="font-semibold text-blue-300">Total Coverage:</span> Combined DAO income from rentals and reserve yield</p>
                  <p><span className="font-semibold text-blue-300">Estimated Ops Cost:</span> Annual operating expenses (utilities, maintenance, cleaning, etc.)</p>
                  <p><span className="font-semibold text-blue-300">Surplus:</span> Total coverage minus estimated operating costs</p>
                </div>
              </div>
              
              <div className="bg-gray-700 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-yellow-300 mb-2">How It Works</h3>
                <p className="text-sm">
                  The calculator automatically determines the required "credits per night" to make each project financially sustainable. 
                  It begins at a baseline value and increases incrementally until the total raised covers all initial costs—property price, 
                  legal/admin fees, and a minimum reserve—while also generating a positive projected surplus.
                </p>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}
      </section>
    );
};

export default Tech; 